import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { getShipments } from "../../Api/shipments";
import {
  formatShipmentStatusLabel,
  getShipmentStatusPillProps,
} from "../../utils";
import useDebounceState from "../../customHooks/useDebounceState";
import { ReactComponent as CloseIcon } from "../../assets/Close.svg";
import { ReactComponent as SearchIcon } from "../../assets/Search.svg";

const PAGE_SIZE = 8;
const MIN_CHARS = 3;

const customerName = shipment =>
  shipment?.deliveryPOC?.name || shipment?.pickupPOC?.name || "";

export default function GlobalShipmentSearch() {
  const navigate = useNavigate();
  const clientUid = useSelector(state => state.filters?.client?.value);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounceState({ value: query, delay: 500 });
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const fetchGen = useRef(0);

  useEffect(() => {
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    const term = (debouncedQuery || "").trim();
    if (term.length < MIN_CHARS) {
      fetchGen.current += 1;
      setResults([]);
      setTotalCount(0);
      setIsLoading(false);
      setHasSearched(false);
      setActiveIndex(-1);
      return;
    }

    const gen = ++fetchGen.current;
    setIsLoading(true);
    setHasSearched(false);
    setIsOpen(true);

    getShipments({
      pageSize: PAGE_SIZE,
      pageNumber: 0,
      search: term,
      clientUid: clientUid || undefined,
    })
      .then(({ shipments, count }) => {
        if (gen !== fetchGen.current) return;
        setResults(shipments || []);
        setTotalCount(count || 0);
        setHasSearched(true);
        setActiveIndex((shipments || []).length === 1 ? 0 : -1);
      })
      .catch(error => {
        if (gen !== fetchGen.current) return;
        console.log(error);
        setResults([]);
        setTotalCount(0);
        setHasSearched(true);
        setActiveIndex(-1);
      })
      .finally(() => {
        if (gen !== fetchGen.current) return;
        setIsLoading(false);
      });
  }, [debouncedQuery, clientUid]);

  const goToShipment = waybill => {
    if (!waybill) return;
    setIsOpen(false);
    setActiveIndex(-1);
    setQuery("");
    setResults([]);
    setTotalCount(0);
    setHasSearched(false);
    navigate(`/shipments/${waybill}`);
  };

  const onKeyDown = event => {
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter")) {
      if ((query || "").trim().length >= MIN_CHARS) setIsOpen(true);
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!results.length) return;
      setIsOpen(true);
      setActiveIndex(prev => (prev + 1) % results.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!results.length) return;
      setIsOpen(true);
      setActiveIndex(prev => (prev <= 0 ? results.length - 1 : prev - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (results.length === 1) {
        goToShipment(results[0].waybill);
        return;
      }
      if (activeIndex >= 0 && results[activeIndex]) {
        goToShipment(results[activeIndex].waybill);
      }
    }
  };

  const showPanel =
    isOpen && (query.trim().length >= MIN_CHARS || isLoading || hasSearched);

  return (
    <div ref={rootRef} className='relative w-full min-w-0'>
      <div className='relative'>
        <span className='absolute inset-y-0 left-3.5 flex items-center pointer-events-none text-primary-main'>
          <SearchIcon className='w-[18px] h-[18px]' />
        </span>
        <input
          ref={inputRef}
          type='search'
          role='combobox'
          aria-expanded={showPanel}
          aria-controls='global-shipment-search-results'
          aria-autocomplete='list'
          aria-activedescendant={
            activeIndex >= 0
              ? `global-shipment-search-option-${activeIndex}`
              : undefined
          }
          autoComplete='off'
          className='block w-full h-11 py-0 pl-11 pr-11 text-sm border border-primary-lightest rounded-xl bg-[#F3F8FF] text-grey-900 placeholder:text-grey-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-lightest focus:border-primary-main [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden'
          placeholder='Search waybill, order, name or phone...'
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (query.trim().length >= MIN_CHARS) setIsOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button
            type='button'
            onClick={() => {
              setQuery("");
              setResults([]);
              setTotalCount(0);
              setHasSearched(false);
              setActiveIndex(-1);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            aria-label='Clear search'
            className='absolute inset-y-0 right-2 flex items-center justify-center w-8 text-grey-400 hover:text-grey-700'>
            <CloseIcon className='w-3.5 h-3.5' />
          </button>
        ) : null}
      </div>

      {showPanel && (
        <div
          id='global-shipment-search-results'
          role='listbox'
          className='absolute z-[60] mt-2 w-full min-w-[20rem] max-h-[24rem] overflow-hidden rounded-xl border border-grey-100 bg-white text-sm shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)]'>
          <div className='flex items-center justify-between gap-3 px-4 py-2.5 border-b border-grey-100'>
            <span className='text-[11px] font-semibold uppercase tracking-[0.12em] text-grey-400'>
              Shipments
            </span>
            {!isLoading && hasSearched ? (
              <span className='text-[11px] font-medium tabular-nums text-grey-500'>
                {totalCount === 1 ? "1 result" : `${totalCount} results`}
              </span>
            ) : null}
          </div>
          <ul className='max-h-[20rem] overflow-auto py-1.5'>
            {isLoading && (
              <li className='px-4 py-3.5 text-grey-500 select-none'>
                Searching…
              </li>
            )}
            {!isLoading && hasSearched && results.length === 0 && (
              <li className='px-4 py-7 text-center text-grey-500 select-none'>
                No shipments found
              </li>
            )}
            {!isLoading &&
              results.map((shipment, index) => {
                const statusPill = getShipmentStatusPillProps(shipment.status);
                const name = customerName(shipment);
                const orderRef = shipment.clientOrderReference;
                return (
                  <li
                    key={shipment.waybill}
                    id={`global-shipment-search-option-${index}`}
                    role='option'
                    aria-selected={index === activeIndex}
                    className={`cursor-pointer px-4 py-3 mx-1.5 rounded-lg ${
                      index === activeIndex
                        ? "bg-grey-50"
                        : "hover:bg-grey-50"
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={event => {
                      event.preventDefault();
                      goToShipment(shipment.waybill);
                    }}>
                    <div className='flex items-center justify-between gap-3 min-w-0'>
                      <div className='min-w-0'>
                        <div className='font-medium text-grey-900 truncate'>
                          #{shipment.waybill}
                          {orderRef ? (
                            <span className='font-normal text-grey-500'>
                              {" "}
                              · {orderRef}
                            </span>
                          ) : null}
                        </div>
                        {name ? (
                          <div className='text-sm text-grey-500 truncate mt-0.5'>
                            {name}
                          </div>
                        ) : null}
                      </div>
                      {shipment.status ? (
                        <span
                          className={`shrink-0 inline-flex items-center rounded-md px-2.5 py-1 text-xs capitalize ${statusPill.className}`}
                          style={statusPill.style}>
                          {formatShipmentStatusLabel(shipment.status)}
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
}
